// pages/admin/NowPaymentsInvoiceDetail.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock,
  User, DollarSign, Bitcoin, Calendar, Copy, ExternalLink,
  Wallet, Zap, AlertTriangle, FileText, QrCode,
  Info, Shield, Mail, Hash, Link, Image
} from 'lucide-react';
import { AdminNowPaymentsAPI, AdminAPIUtils } from '../services/api';

const NowPaymentsInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [creditAmount, setCreditAmount] = useState('');

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AdminNowPaymentsAPI.getInvoice(invoiceId);
      
      if (response.success) {
        setInvoice(response.invoice);
      } else {
        setError(response.error || 'Failed to load invoice');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setProcessing(true);
      const response = await AdminNowPaymentsAPI.refreshInvoice(invoiceId);
      
      if (response.success) {
        alert(`✅ Invoice status updated: ${response.old_status} → ${response.new_status}`);
        await fetchInvoice();
      } else {
        alert('❌ Failed to refresh: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Refresh error:', err);
      alert('❌ Failed to refresh invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    try {
      setProcessing(true);
      const response = await AdminNowPaymentsAPI.cancelInvoice(invoiceId, notes);
      
      if (response.success) {
        alert('✅ Invoice cancelled successfully');
        setShowCancelModal(false);
        setNotes('');
        await fetchInvoice();
      } else {
        alert('❌ Failed to cancel: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Cancel error:', err);
      alert('❌ Failed to cancel invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualCredit = async () => {
    try {
      setProcessing(true);
      const response = await AdminNowPaymentsAPI.manualCredit({
        invoice_id: invoiceId,
        user_id: invoice.user_id,
        amount: parseFloat(creditAmount),
        notes: notes
      });
      
      if (response.success) {
        alert(`✅ User credited with ${creditAmount} BTC`);
        setShowCreditModal(false);
        setCreditAmount('');
        setNotes('');
        await fetchInvoice();
      } else {
        alert('❌ Failed to credit: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Credit error:', err);
      alert('❌ Failed to credit user');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const config = {
      waiting: { 
        bg: 'bg-yellow-500/10', 
        text: 'text-yellow-400', 
        border: 'border-yellow-500/20',
        icon: <Clock className="w-4 h-4" />,
        label: 'Waiting for Payment'
      },
      confirming: { 
        bg: 'bg-blue-500/10', 
        text: 'text-blue-400', 
        border: 'border-blue-500/20',
        icon: <RefreshCw className="w-4 h-4 animate-spin" />,
        label: 'Confirming'
      },
      partially_paid: { 
        bg: 'bg-orange-500/10', 
        text: 'text-orange-400', 
        border: 'border-orange-500/20',
        icon: <AlertTriangle className="w-4 h-4" />,
        label: 'Partially Paid'
      },
      confirmed: { 
        bg: 'bg-emerald-500/10', 
        text: 'text-emerald-400', 
        border: 'border-emerald-500/20',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Confirmed'
      },
      finished: { 
        bg: 'bg-green-500/10', 
        text: 'text-green-400', 
        border: 'border-green-500/20',
        icon: <CheckCircle className="w-4 h-4" />,
        label: '✅ Finished'
      },
      expired: { 
        bg: 'bg-gray-500/10', 
        text: 'text-gray-400', 
        border: 'border-gray-500/20',
        icon: <AlertTriangle className="w-4 h-4" />,
        label: 'Expired'
      },
      failed: { 
        bg: 'bg-rose-500/10', 
        text: 'text-rose-400', 
        border: 'border-rose-500/20',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Failed'
      },
      refunded: { 
        bg: 'bg-purple-500/10', 
        text: 'text-purple-400', 
        border: 'border-purple-500/20',
        icon: <RefreshCw className="w-4 h-4" />,
        label: 'Refunded'
      },
      manually_credited: { 
        bg: 'bg-cyan-500/10', 
        text: 'text-cyan-400', 
        border: 'border-cyan-500/20',
        icon: <Zap className="w-4 h-4" />,
        label: 'Manual Credit'
      }
    };
    
    const configItem = config[status] || config.waiting;
    
    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${configItem.bg} ${configItem.text} ${configItem.border} border`}>
        {configItem.icon}
        <span className="font-medium">{configItem.label}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const canCancel = invoice && ['waiting', 'confirming', 'partially_paid'].includes(invoice.status);
  const canCredit = invoice && ['failed', 'expired'].includes(invoice.status);
  const canRefresh = invoice && invoice.status !== 'finished' && invoice.status !== 'manually_credited';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading invoice details...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-12 text-center">
        <AlertTriangle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Invoice Not Found</h3>
        <p className="text-charcoal-gray mb-6">{error || 'The invoice you\'re looking for doesn\'t exist'}</p>
        <button
          onClick={() => navigate('/admin/nowpayments')}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl hover:scale-105 transition-all duration-300"
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/nowpayments')}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Invoice Details
            </h1>
            <p className="text-charcoal-gray text-sm font-mono">{invoice.invoice_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canRefresh && (
            <button
              onClick={handleRefresh}
              disabled={processing}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${processing ? 'animate-spin' : ''}`} />
              Refresh Status
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={processing}
              className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              Cancel Invoice
            </button>
          )}
          {canCredit && (
            <button
              onClick={() => {
                setCreditAmount(invoice.pay_amount || '');
                setShowCreditModal(true);
              }}
              disabled={processing}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
            >
              <Wallet className="w-4 h-4" />
              Manual Credit
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {getStatusBadge(invoice.status)}
            <span className="text-charcoal-gray text-sm">
              Created {formatDate(invoice.created_at)}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {invoice.completed_at && (
              <span className="text-emerald-400">
                ✅ Completed {formatDate(invoice.completed_at)}
              </span>
            )}
            {invoice.expired_at && (
              <span className="text-rose-400">
                ⏰ Expired {formatDate(invoice.expired_at)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Invoice Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Card */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Payment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/30 rounded-xl p-4">
                <p className="text-charcoal-gray text-sm">Requested Amount</p>
                <p className="text-2xl font-bold text-white">
                  {AdminAPIUtils.formatBTC(invoice.price_amount)} {invoice.price_currency}
                </p>
              </div>
              <div className="bg-gray-800/30 rounded-xl p-4">
                <p className="text-charcoal-gray text-sm">Amount to Pay</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {AdminAPIUtils.formatBTC(invoice.pay_amount)} {invoice.pay_currency}
                </p>
              </div>
              {invoice.paid_amount && (
                <div className="bg-gray-800/30 rounded-xl p-4 col-span-2">
                  <p className="text-charcoal-gray text-sm">Actual Paid Amount</p>
                  <p className="text-2xl font-bold text-green-400">
                    {AdminAPIUtils.formatBTC(invoice.paid_amount)} {invoice.paid_currency || invoice.pay_currency}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* User Info Card */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              User Information
            </h3>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                {invoice.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-xl font-semibold text-white">{invoice.username || 'Unknown'}</p>
                <p className="text-charcoal-gray flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {invoice.email || 'No email'}
                </p>
                <p className="text-charcoal-gray text-sm">User ID: {invoice.user_id}</p>
              </div>
            </div>
          </div>

          {/* Address Info */}
          {invoice.pay_address && (
            <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Bitcoin className="w-5 h-5 text-yellow-400" />
                Payment Address
              </h3>
              <div className="bg-gray-800/50 rounded-xl p-4 flex items-center gap-3">
                <code className="text-yellow-400 text-sm break-all flex-1 font-mono">
                  {invoice.pay_address}
                </code>
                <button
                  onClick={() => copyToClipboard(invoice.pay_address)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                  title="Copy address"
                >
                  <Copy className="w-4 h-4 text-white" />
                </button>
              </div>
              {invoice.invoice_url && (
                <a
                  href={invoice.invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
                >
                  <Link className="w-4 h-4" />
                  View Invoice on NOWPayments
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Details & Actions */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-charcoal-gray" />
              Quick Info
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-charcoal-gray text-sm">Invoice ID</span>
                <span className="text-white font-mono text-sm">{invoice.invoice_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-charcoal-gray text-sm">Order ID</span>
                <span className="text-white font-mono text-sm">{invoice.order_id || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-charcoal-gray text-sm">Payment ID</span>
                <span className="text-white font-mono text-sm">{invoice.payment_id || '-'}</span>
              </div>
              {invoice.confirmations > 0 && (
                <div className="flex justify-between">
                  <span className="text-charcoal-gray text-sm">Confirmations</span>
                  <span className="text-green-400">{invoice.confirmations}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-charcoal-gray text-sm">Created</span>
                <span className="text-white text-sm">{formatDate(invoice.created_at)}</span>
              </div>
              {invoice.updated_at && (
                <div className="flex justify-between">
                  <span className="text-charcoal-gray text-sm">Last Updated</span>
                  <span className="text-white text-sm">{formatDate(invoice.updated_at)}</span>
                </div>
              )}
            </div>
          </div>

          {/* QR Code */}
          {invoice.qr_code && (
            <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 text-center">
              <h3 className="font-bold text-white mb-4 flex items-center justify-center gap-2">
                <QrCode className="w-5 h-5" />
                QR Code
              </h3>
              <img 
                src={invoice.qr_code} 
                alt="Payment QR Code" 
                className="mx-auto w-48 h-48 rounded-xl"
              />
            </div>
          )}

          {/* Admin Actions */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Admin Actions
            </h3>
            <div className="space-y-2">
              {canRefresh && (
                <button
                  onClick={handleRefresh}
                  disabled={processing}
                  className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${processing ? 'animate-spin' : ''}`} />
                  Refresh Status
                </button>
              )}
              {canCancel && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={processing}
                  className="w-full px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Invoice
                </button>
              )}
              {canCredit && (
                <button
                  onClick={() => {
                    setCreditAmount(invoice.pay_amount || '');
                    setShowCreditModal(true);
                  }}
                  disabled={processing}
                  className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                >
                  <Wallet className="w-4 h-4" />
                  Manual Credit User
                </button>
              )}
              <button
                onClick={() => window.open(invoice.invoice_url, '_blank')}
                className="w-full px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
              >
                <ExternalLink className="w-4 h-4" />
                View on NOWPayments
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-400" />
                Cancel Invoice
              </h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <p className="text-gray-300">
                Are you sure you want to cancel this invoice?
              </p>
              <div className="bg-gray-800/30 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-charcoal-gray">Invoice</span>
                  <span className="text-white font-mono text-sm">{invoice.invoice_id}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-charcoal-gray">Amount</span>
                  <span className="text-emerald-400 font-bold">
                    {AdminAPIUtils.formatBTC(invoice.pay_amount)} {invoice.pay_currency}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Cancellation Reason *
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why is this invoice being cancelled?"
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors resize-none"
                  rows="3"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCancel}
                disabled={processing || !notes}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Confirm Cancellation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Credit Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-400" />
                Manual Credit
              </h3>
              <button
                onClick={() => setShowCreditModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <p className="text-gray-300">
                Manually credit the user for this failed/expired invoice.
              </p>
              <div className="bg-gray-800/30 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-charcoal-gray">User</span>
                  <span className="text-white font-medium">{invoice.username}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-charcoal-gray">Original Amount</span>
                  <span className="text-emerald-400 font-bold">
                    {AdminAPIUtils.formatBTC(invoice.pay_amount)} {invoice.pay_currency}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Amount to Credit (BTC) *
                </label>
                <input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  step="0.00000001"
                  min="0"
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for manual credit..."
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors resize-none"
                  rows="2"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCreditModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleManualCredit}
                disabled={processing || !creditAmount || parseFloat(creditAmount) <= 0}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Credit User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NowPaymentsInvoiceDetail;